import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Polygon, Polyline, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { dnoAPI } from '../lib/api';
import { Loader2, MapPinned, Hand, PenLine, Spline, Undo2, Eraser, Save, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const UK_CENTER = [54.5, -3.0];

const meterIcon = L.divIcon({
  className: '',
  html: '<div style="width:34px;height:34px;border-radius:50%;background:#fff;border:3px solid #dc2626;display:flex;align-items:center;justify-content:center;font:700 18px/1 Arial,sans-serif;color:#dc2626;box-shadow:0 2px 6px rgba(0,0,0,.5);cursor:grab">M</div>',
  iconSize: [34, 34], iconAnchor: [17, 17],
});
const vertexIcon = L.divIcon({
  className: '',
  html: '<div style="width:12px;height:12px;border-radius:50%;background:#fff;border:2px solid #dc2626;box-shadow:0 1px 3px rgba(0,0,0,.6);cursor:grab"></div>',
  iconSize: [12, 12], iconAnchor: [6, 6],
});

const FitOnce = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds) map.fitBounds([[bounds.south, bounds.west], [bounds.north, bounds.east]], { animate: false });
  }, [map, bounds]);
  return null;
};

const DrawLayer = ({ mode, onAddPoint, onFreehand, onSetMeter }) => {
  const map = useMap();
  const drawing = useRef(false);
  const buf = useRef([]);
  const lastPx = useRef(null);

  useEffect(() => {
    if (mode === 'freehand') { map.dragging.disable(); map.getContainer().style.cursor = 'crosshair'; }
    else { map.dragging.enable(); map.getContainer().style.cursor = mode === 'pan' ? '' : 'crosshair'; }
    return () => { map.dragging.enable(); map.getContainer().style.cursor = ''; };
  }, [map, mode]);

  useMapEvents({
    click(e) {
      if (mode === 'points') onAddPoint([e.latlng.lat, e.latlng.lng]);
      if (mode === 'meter') onSetMeter([e.latlng.lat, e.latlng.lng]);
    },
    mousedown(e) {
      if (mode !== 'freehand') return;
      drawing.current = true; buf.current = [[e.latlng.lat, e.latlng.lng]];
      lastPx.current = e.containerPoint;
      onFreehand(buf.current, false);
    },
    mousemove(e) {
      if (!drawing.current) return;
      if (lastPx.current && e.containerPoint.distanceTo(lastPx.current) < 6) return;
      lastPx.current = e.containerPoint;
      buf.current = [...buf.current, [e.latlng.lat, e.latlng.lng]];
      onFreehand(buf.current, false);
    },
    mouseup() {
      if (!drawing.current) return;
      drawing.current = false;
      onFreehand(buf.current, true);
    },
  });
  return null;
};

const TOOLS = [
  { id: 'pan', label: 'Pan', icon: Hand, hint: 'Drag to move, scroll to zoom' },
  { id: 'points', label: 'Point boundary', icon: Spline, hint: 'Click to place corner points around the property' },
  { id: 'freehand', label: 'Pen', icon: PenLine, hint: 'Hold the mouse down and draw around the property' },
  { id: 'meter', label: 'Meter (M)', icon: MapPinned, hint: 'Click where the meter / cut-out is — drag to fine-tune' },
];

/** Full-screen satellite map: draw the site boundary + drop an "M" meter marker. */
export const BoundaryMapDialog = ({ customer, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [providers, setProviders] = useState([]);
  const [provider, setProvider] = useState('esri');
  const [center, setCenter] = useState(null);
  const [initialBounds, setInitialBounds] = useState(null);
  const [polygon, setPolygon] = useState([]);
  const [meter, setMeter] = useState(null);
  const [mode, setMode] = useState('pan');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const mapRef = useRef(null);

  const prov = useMemo(() => providers.find((p) => p.id === provider) || providers[0], [providers, provider]);

  const openDialog = async () => {
    if (!customer?.id) { toast.error('Select a customer first'); return; }
    setOpen(true);
    setLoading(true);
    setMode('pan');
    try {
      const [{ data: provs }, { data: existing }] = await Promise.all([
        dnoAPI.boundaryMapProviders(), dnoAPI.getBoundaryMap(customer.id),
      ]);
      setProviders(provs);
      setSaved(existing.document || null);
      const g = existing.geometry;
      if (g) {
        setPolygon(g.polygon || []);
        setMeter(g.meter || null);
        setProvider(g.provider || 'esri');
        setInitialBounds(g.bounds || null);
        setCenter(g.center || [(g.bounds.south + g.bounds.north) / 2, (g.bounds.west + g.bounds.east) / 2]);
      } else {
        setPolygon([]); setMeter(null); setInitialBounds(null); setProvider('esri');
        const pc = existing.postcode || customer.postcode;
        let c = null;
        if (pc) {
          try {
            const { data } = await dnoAPI.geocodePostcode(pc);
            if (data.latitude && data.longitude) c = [data.latitude, data.longitude];
          } catch { /* fall through */ }
        }
        if (!c) toast.warning('Could not locate the postcode — pan/zoom to the property manually');
        setCenter(c || UK_CENTER);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open the boundary map');
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const undo = () => setPolygon((p) => p.slice(0, -1));
  const clearAll = () => { setPolygon([]); setMeter(null); };

  const save = async () => {
    const map = mapRef.current;
    if (!map) return;
    if (polygon.length < 3 && !meter) { toast.error('Draw a boundary and/or place the meter marker first'); return; }
    const b = map.getBounds();
    const c = map.getCenter();
    setSaving(true);
    try {
      const { data } = await dnoAPI.saveBoundaryMap(customer.id, {
        bounds: { south: b.getSouth(), west: b.getWest(), north: b.getNorth(), east: b.getEast() },
        zoom: Math.round(map.getZoom()),
        provider, polygon, meter, center: [c.lat, c.lng],
      });
      setSaved({ id: data.document_id, filename: data.filename, preview_url: data.preview_url });
      toast.success('Boundary map saved to the customer file and attached to the application');
      if (onSaved) onSaved(data.boundary_file_url, data.filename, data.document_id);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the boundary map');
    } finally {
      setSaving(false);
    }
  };

  const tool = TOOLS.find((t) => t.id === mode);

  return (
    <>
      <button type="button" onClick={openDialog}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded cursor-pointer border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50 transition-colors"
        data-testid="boundary-map-btn">
        <MapPinned className="w-3 h-3" /> Boundary Map (optional)
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[96vw] w-[96vw] h-[92vh] p-0 flex flex-col gap-0 overflow-hidden" data-testid="boundary-map-dialog">
          <DialogHeader className="px-4 pt-3 pb-2 border-b">
            <div className="flex items-center justify-between gap-3 flex-wrap pr-8">
              <div>
                <DialogTitle className="text-base">Site Boundary Map</DialogTitle>
                <DialogDescription className="text-xs">
                  Outline the property and drop an <b>M</b> on the meter. Saved as a PNG on the customer file (tag <code>dno_boundary_map</code>) and attached to the DNO application.
                </DialogDescription>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Imagery</span>
                <Select value={provider} onValueChange={setProvider}>
                  <SelectTrigger className="h-8 w-56 text-xs" data-testid="boundary-map-provider"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {providers.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`boundary-map-provider-${p.id}`}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </DialogHeader>

          <div className="flex items-center gap-1.5 px-4 py-2 border-b bg-slate-50 flex-wrap">
            {TOOLS.map(({ id, label, icon: Icon }) => (
              <button key={id} type="button" onClick={() => setMode(id)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border transition-colors ${mode === id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'}`}
                data-testid={`boundary-tool-${id}`}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
            <span className="text-xs text-slate-500 ml-2 hidden md:inline" data-testid="boundary-tool-hint">{tool?.hint}</span>
            <div className="ml-auto flex items-center gap-1.5">
              <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={undo} disabled={!polygon.length} data-testid="boundary-undo-btn">
                <Undo2 className="w-3.5 h-3.5 mr-1" /> Undo point
              </Button>
              <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={clearAll} disabled={!polygon.length && !meter} data-testid="boundary-clear-btn">
                <Eraser className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            </div>
          </div>

          <div className="flex-1 relative min-h-0" data-testid="boundary-map-canvas">
            {loading || !center ? (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-600 gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Locating the property…
              </div>
            ) : (
              <MapContainer ref={mapRef} center={center} zoom={initialBounds ? 18 : 19} maxZoom={22}
                style={{ height: '100%', width: '100%' }} scrollWheelZoom doubleClickZoom={false} attributionControl={false}>
                {prov && (
                  <TileLayer key={prov.id} url={prov.url} maxZoom={22} maxNativeZoom={prov.max_zoom} attribution={prov.attribution} />
                )}
                {initialBounds && <FitOnce bounds={initialBounds} />}
                <DrawLayer mode={mode}
                  onAddPoint={(pt) => setPolygon((p) => [...p, pt])}
                  onFreehand={(pts, done) => setPolygon(done && pts.length > 2 ? pts : pts)}
                  onSetMeter={setMeter} />
                {polygon.length >= 3 ? (
                  <Polygon positions={polygon} pathOptions={{ color: '#ff1e1e', weight: 4, fillColor: '#ff2828', fillOpacity: 0.18 }} />
                ) : polygon.length === 2 ? (
                  <Polyline positions={polygon} pathOptions={{ color: '#ff1e1e', weight: 4 }} />
                ) : null}
                {polygon.length <= 60 && polygon.map((pt, i) => (
                  <Marker key={`v-${i}`} position={pt} icon={vertexIcon} draggable
                    eventHandlers={{ dragend: (e) => { const ll = e.target.getLatLng(); setPolygon((p) => p.map((q, j) => (j === i ? [ll.lat, ll.lng] : q))); } }} />
                ))}
                {meter && (
                  <Marker position={meter} icon={meterIcon} draggable
                    eventHandlers={{ dragend: (e) => { const ll = e.target.getLatLng(); setMeter([ll.lat, ll.lng]); } }} />
                )}
              </MapContainer>
            )}
            {prov && (
              <div className="absolute bottom-1 right-1 z-[1000] bg-white/80 text-[10px] text-slate-600 px-1.5 py-0.5 rounded">{prov.attribution}</div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t bg-white flex-wrap">
            <div className="text-xs text-slate-600 flex items-center gap-3">
              <span data-testid="boundary-point-count">{polygon.length} boundary point{polygon.length === 1 ? '' : 's'}</span>
              <span data-testid="boundary-meter-status">{meter ? 'Meter placed' : 'Meter not placed'}</span>
              {saved && (
                <span className="inline-flex items-center gap-1 text-emerald-700" data-testid="boundary-saved-status">
                  <CheckCircle2 className="w-3.5 h-3.5" /> On file: {saved.filename}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="boundary-cancel-btn">Cancel</Button>
              <Button type="button" onClick={save} disabled={saving || loading} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="boundary-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                {saving ? 'Rendering…' : 'Save boundary map'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BoundaryMapDialog;
